import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const INR = (n) => { const v = Number(n||0); return `${v<0?"-":""}₹ ${Math.abs(v).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`; };
const NUM = (n) => Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const DATE = (d) => { try { return new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); } catch { return d; } };
const INK=[15,23,42], MUTED=[71,85,105], GREY=[241,245,249], HEAD=[51,65,85], LINE=[203,213,225];
const L=40, R=555, META_X=330;
const FONT = "LedgerSans";
const TABLE = { styles:{font:FONT, fontSize:9, cellPadding:4, textColor:INK}, headStyles:{fillColor:GREY, textColor:HEAD, fontStyle:"bold"}, footStyles:{fillColor:GREY, textColor:INK, fontStyle:"bold"}, margin:{left:L, right:40} };

let fontCache = null;
async function loadFonts() {
  if (fontCache) return fontCache;
  const toB64 = (buf) => { let s = ""; const bytes = new Uint8Array(buf); for (let i=0;i<bytes.length;i++) s += String.fromCharCode(bytes[i]); return btoa(s); };
  const [reg, bold] = await Promise.all(["/fonts/LedgerSans-Regular.ttf", "/fonts/LedgerSans-Bold.ttf"].map(u => fetch(u).then(r => { if (!r.ok) throw new Error("font"); return r.arrayBuffer(); })));
  fontCache = { reg: toB64(reg), bold: toB64(bold) };
  return fontCache;
}

async function newDoc() {
  const doc = new jsPDF({ unit:"pt", format:"a4" });
  const f = await loadFonts();
  doc.addFileToVFS("LedgerSans-Regular.ttf", f.reg); doc.addFont("LedgerSans-Regular.ttf", FONT, "normal");
  doc.addFileToVFS("LedgerSans-Bold.ttf", f.bold); doc.addFont("LedgerSans-Bold.ttf", FONT, "bold");
  doc.setFont(FONT, "normal");
  return doc;
}

function drawHeader(doc, company, title) {
  doc.setFont(FONT,"bold"); doc.setFontSize(16); doc.setTextColor(...INK);
  doc.text(company?.name || "My Company", L, 45);
  doc.setFont(FONT,"normal"); doc.setFontSize(9); doc.setTextColor(...MUTED);
  [company?.address, company?.phone, company?.email, company?.gstin ? `GSTIN: ${company.gstin}` : ""].filter(Boolean).forEach((l,i)=>doc.text(String(l), L, 62 + i*12));
  doc.setFont(FONT,"bold"); doc.setFontSize(14); doc.setTextColor(...INK);
  doc.text(title, R, 45, { align: "right" });
  doc.setDrawColor(...LINE); doc.line(L, 120, R, 120);
}

function drawParty(doc, label, customer, y) {
  doc.setFont(FONT,"bold"); doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text(label, L, y);
  doc.setFont(FONT,"normal"); doc.setFontSize(10); doc.setTextColor(...INK);
  [customer?.name, customer?.address, customer?.phone, customer?.gstin ? `GSTIN: ${customer.gstin}` : ""].filter(Boolean).forEach((l,i)=>doc.text(String(l), L, y + 16 + i*13));
}

function drawMeta(doc, pairs, y) {
  pairs.forEach(([k,v],i) => {
    doc.setFont(FONT,"bold"); doc.setFontSize(8); doc.setTextColor(...MUTED); doc.text(k, META_X, y + i*16);
    doc.setFont(FONT,"normal"); doc.setFontSize(10); doc.setTextColor(...INK); doc.text(String(v ?? "-"), R, y + i*16, {align:"right"});
  });
}

function drawTotals(doc, rows, y) {
  rows.forEach(([label, val, bold, rule]) => {
    if (rule) { doc.setDrawColor(...LINE); doc.line(META_X, y-10, R, y-10); }
    doc.setFont(FONT, bold ? "bold" : "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
    doc.text(label, META_X, y); doc.text(INR(val), R, y, {align:"right"}); y += 16;
  });
  return y;
}

// Shared subtotal label incl. pieces count, e.g. "Total: 62.00 Mtrs / 4 Pcs" or "Total: 62.00 Pcs"
function annexTotal(l) {
  const rows = l.breakdown_rows || [];
  const pieces = rows.reduce((s, b) => s + Number(b.mult || 0), 0);
  const P = (n) => Number.isInteger(Number(n)) ? String(Number(n)) : NUM(n);
  const isPcs = String(l.unit || "").trim().toLowerCase() === "pcs";
  return isPcs ? `Total: ${NUM(l.qty)} Pcs` : `Total: ${NUM(l.qty)} ${l.unit} / ${P(pieces)} Pcs`;
}

// Annexure grid: 2 fixed-width columns per row (matches the A5 browser-print card grid).
export const ANNEX_COLS = 2, ANNEX_ROWS = 8;
export function annexureBlocks(lines) {
  const cols = [];
  (lines||[]).forEach((l,i) => {
    const rows = l.breakdown_rows||[]; if (!rows.length) return;
    const cells = rows.map(b=>({ base: NUM(b.base), mult: NUM(b.mult), sub: NUM(Number(b.base)*Number(b.mult)) }));
    const n = Math.ceil(cells.length / ANNEX_ROWS);
    for (let c=0; c<n; c++) cols.push({ index:i, unit:l.unit, tag:`#${i+1} ${l.product_name}${n>1?` (${c+1}/${n})`:""}`, total:annexTotal(l), cells: cells.slice(c*ANNEX_ROWS, (c+1)*ANNEX_ROWS) });
  });
  const blocks = [];
  for (let b=0; b<cols.length; b+=ANNEX_COLS) blocks.push(cols.slice(b, b+ANNEX_COLS));
  return blocks;
}

export function productRuns(cols) {
  const runs = [];
  cols.forEach(c => {
    const last = runs[runs.length - 1];
    if (last && last[0].index === c.index) last.push(c);
    else runs.push([c]);
  });
  return runs;
}

// Cards for the on-screen / browser-print annexure (A5). One card per 8-row chunk,
// in item order. Any line with a quantity breakdown is included (even a single row).
export function annexureCards(lines) {
  const cards = [];
  (lines || []).forEach((l, i) => {
    const rows = l.breakdown_rows || [];
    if (!rows.length) return;
    const cells = rows.map(b => ({ base: NUM(b.base), mult: NUM(b.mult), sub: NUM(Number(b.base) * Number(b.mult)) }));
    const n = Math.ceil(cells.length / ANNEX_ROWS);
    for (let c = 0; c < n; c++) {
      cards.push({
        index: i,
        unit: l.unit,
        tag: `#${i + 1} ${l.product_name}${n > 1 ? ` (${c + 1}/${n})` : ""}`,
        isLast: c === n - 1,
        total: annexTotal(l),
        cells: cells.slice(c * ANNEX_ROWS, (c + 1) * ANNEX_ROWS),
      });
    }
  });
  return cards;
}

function drawAnnexure(doc, invoice, y) {
  const blocks = annexureBlocks(invoice.lines);
  if (!blocks.length) return y;
  if (y > 640) { doc.addPage(); y = 50; }
  doc.setFont(FONT,"bold"); doc.setFontSize(10); doc.setTextColor(...INK);
  doc.text("ANNEXURE – QUANTITY BREAKDOWN", L, y);
  y += 8;
  const ratio = [42, 10, 32, 44], ratioSum = 128, GAP = 8, groupW = 515 / ANNEX_COLS;
  blocks.forEach(cols => {
    let colsSoFar = 0, maxY = y;
    productRuns(cols).forEach((run, ri) => {
      const left = L + colsSoFar * groupW + ri * GAP;
      const columnStyles = {};
      run.forEach((_, g) => ratio.forEach((w,k) => { columnStyles[g*4+k] = { cellWidth: groupW * w / ratioSum, halign: k===1 ? "center" : "right" }; }));
      const head = [
        run.map(c => ({ content: `${c.tag}  ·  ${c.total}`, colSpan: 4, styles: { halign: "left", fontStyle: "bold", fillColor: GREY, textColor: INK, fontSize: 8 } })),
        run.flatMap(c => [`${c.unit}`, "X", "Pieces", "Subtotal"]),
      ];
      const nRows = Math.max(...run.map(c => c.cells.length));
      const body = [...Array(nRows)].map((_, r) => run.flatMap(c => { const x = c.cells[r]; return x ? [x.base, "×", x.mult, x.sub] : ["", "", "", ""]; }));
      autoTable(doc, { startY: y, head, body, theme:"grid",
        styles:{ font:FONT, fontSize:8, cellPadding:2.5, textColor:INK, lineColor:LINE, lineWidth:0.5 },
        headStyles:{ fillColor:GREY, textColor:HEAD, fontStyle:"bold", fontSize:7 },
        columnStyles, margin:{ left: left, right: 40 }, tableWidth: groupW * run.length,
        didParseCell: (d) => { if (d.section === "head" && d.row.index === 1) d.cell.styles.halign = (d.column.index % 4 === 1) ? "center" : "right"; } });
      maxY = Math.max(maxY, doc.lastAutoTable.finalY);
      colsSoFar += run.length;
    });
    y = maxY + 8;
  });
  return y + 4;
}

export async function buildInvoicePDF(company, invoice, customer, opts = {}) {
  const doc = await newDoc();
  drawHeader(doc, company, (company?.invoice_title || "INVOICE").toUpperCase());
  drawParty(doc, "BILL TO", customer, 140);
  drawMeta(doc, [["INVOICE #", invoice.invoice_number], ["DATE", DATE(invoice.date)]], 140);
  autoTable(doc, { ...TABLE, startY: 220, theme: "plain",
    head: [["#", "Item", "Qty", "Unit", "Rate", "Amount"]],
    body: invoice.lines.map((l,i)=>[i+1, l.product_name, NUM(l.qty), l.unit, NUM(l.rate), NUM(l.line_total)]),
    styles: { ...TABLE.styles, fontSize: 10, cellPadding: 5 },
    headStyles: { fillColor: false, textColor: INK, fontStyle: "bold", lineWidth: { top: 0.75, bottom: 0.75 }, lineColor: INK },
    bodyStyles: { fillColor: false, lineWidth: { bottom: 0.3 }, lineColor: LINE },
    columnStyles: { 0:{cellWidth:28}, 2:{halign:"right", cellWidth:70}, 3:{cellWidth:55}, 4:{halign:"right", cellWidth:80}, 5:{halign:"right", cellWidth:95} },
  });
  let y = doc.lastAutoTable.finalY + 14 * 3 + 18;
  const rows = [["Subtotal", invoice.subtotal]];
  if (Number(invoice.shipping_fee)>0) rows.push(["Shipping Fees", invoice.shipping_fee]);
  rows.push(["Invoice Total", invoice.total, true, true]);
  if (!opts.withoutBalances) {
    rows.push(["Previous Balance", invoice.previous_balance]);
    rows.push(["Total Outstanding", invoice.total_outstanding]);
    if (Number(invoice.payment_received)>0) rows.push(["Payment Received", -Number(invoice.payment_received)]);
    rows.push(["Net Closing Balance", invoice.net_closing_balance, true, true]);
  }
  y = drawTotals(doc, rows, y);
  y = drawAnnexure(doc, invoice, y + 8);
  if (invoice.notes) { if (y > 760) { doc.addPage(); y = 50; } doc.setFont(FONT,"normal"); doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text(`Notes: ${invoice.notes}`, L, y + 6); }
  return doc;
}

export async function buildReceiptPDF(company, receipt, customer) {
  const doc = await newDoc();
  drawHeader(doc, company, "RECEIPT");
  drawParty(doc, "RECEIVED FROM", customer, 140);
  drawMeta(doc, [["RECEIPT #", receipt.receipt_number], ["DATE", DATE(receipt.date)]], 140);
  autoTable(doc, { ...TABLE, startY: 220, head: [["Particulars", "Amount"]],
    body: [[`Payment received via ${receipt.mode}${receipt.reference ? ` (Ref: ${receipt.reference})` : ""}${receipt.linked_invoice_id ? " against invoice" : ""}`, INR(receipt.amount)]],
    foot: [["Total Received", INR(receipt.amount)]], columnStyles: { 1:{halign:"right", cellWidth:130} } });
  let y = doc.lastAutoTable.finalY + 24;
  if (receipt.notes) { doc.setFont(FONT,"normal"); doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text(receipt.notes, L, y); y += 30; }
  doc.setFont(FONT,"normal"); doc.setFontSize(9); doc.setTextColor(...MUTED);
  doc.text(`For ${company?.name || ""}`, R, y + 40, {align:"right"}); doc.text("Authorised Signatory", R, y + 70, {align:"right"});
  return doc;
}

export async function buildJournalPDF(company, entry, customer) {
  const doc = await newDoc();
  drawHeader(doc, company, "JOURNAL VOUCHER");
  drawParty(doc, "ACCOUNT", customer, 140);
  drawMeta(doc, [["VOUCHER #", entry.voucher_number], ["DATE", DATE(entry.date)], ["TYPE", entry.type]], 140);
  autoTable(doc, { ...TABLE, startY: 230, head: [["Particulars", "Debit", "Credit"]],
    body: [[`${customer?.name || ""} A/c — ${entry.type}${entry.reference ? ` (Ref: ${entry.reference})` : ""}`, entry.debit ? INR(entry.debit) : "-", entry.credit ? INR(entry.credit) : "-"]],
    foot: [["Total", entry.debit ? INR(entry.debit) : "-", entry.credit ? INR(entry.credit) : "-"]],
    columnStyles: { 1:{halign:"right", cellWidth:110}, 2:{halign:"right", cellWidth:110} } });
  let y = doc.lastAutoTable.finalY + 22;
  doc.setFont(FONT,"bold"); doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text("NARRATION", L, y);
  doc.setFont(FONT,"normal"); doc.setFontSize(10); doc.setTextColor(...INK); doc.text(entry.narration || entry.type, L, y + 14, { maxWidth: 500 });
  return doc;
}

export async function buildStatementPDF(company, stmt) {
  const doc = await newDoc();
  drawHeader(doc, company, "STATEMENT OF ACCOUNT");
  drawParty(doc, "CUSTOMER", stmt.customer, 140);
  const period = stmt.from_date || stmt.to_date ? `${stmt.from_date ? DATE(stmt.from_date) : "Start"} – ${stmt.to_date ? DATE(stmt.to_date) : DATE(new Date())}` : "All transactions";
  drawMeta(doc, [["PERIOD", period], ["CLOSING BALANCE", INR(stmt.closing_balance)]], 140);
  const body = [["", "", "", "Opening Balance", "", "", INR(stmt.opening_balance)]];
  stmt.entries.forEach(e => body.push([DATE(e.date), e.type, e.ref, e.particulars, e.debit?INR(e.debit):"-", e.credit?INR(e.credit):"-", INR(e.balance)]));
  autoTable(doc, { ...TABLE, startY: 215,
    head: [["Date","Voucher Type","Ref No","Particulars","Debit","Credit","Balance"]], body,
    foot: [["","","","Closing Balance", INR(stmt.total_debit), INR(stmt.total_credit), INR(stmt.closing_balance)]],
    styles:{...TABLE.styles, fontSize:8}, columnStyles:{ 4:{halign:"right"},5:{halign:"right"},6:{halign:"right"} } });
  return doc;
}

export async function buildDayBookPDF(company, book) {
  const doc = await newDoc();
  drawHeader(doc, company, "DAY BOOK");
  const period = book.from_date || book.to_date ? `${book.from_date ? DATE(book.from_date) : "Start"} – ${book.to_date ? DATE(book.to_date) : DATE(new Date())}` : "All transactions";
  drawMeta(doc, [["PERIOD", period], ["TOTAL DEBIT", INR(book.total_debit)], ["TOTAL CREDIT", INR(book.total_credit)]], 140);
  autoTable(doc, { ...TABLE, startY: 200,
    head: [["Date","Voucher Type","Ref No","Customer","Particulars","Debit","Credit"]],
    body: book.entries.map(e => [DATE(e.date), e.type, e.ref, e.customer_name, e.particulars, e.debit?INR(e.debit):"-", e.credit?INR(e.credit):"-"]),
    foot: [["","","","","Totals", INR(book.total_debit), INR(book.total_credit)]],
    styles:{...TABLE.styles, fontSize:8}, columnStyles:{ 0:{cellWidth:58}, 1:{cellWidth:62}, 2:{cellWidth:62}, 5:{halign:"right", cellWidth:70}, 6:{halign:"right", cellWidth:70} } });
  return doc;
}

export async function buildTablePDF(company, title, meta, head, body, foot, numCols = []) {
  const doc = await newDoc();
  drawHeader(doc, company, title);
  drawMeta(doc, meta, 140);
  const columnStyles = Object.fromEntries(numCols.map(c => [c, { halign: "right" }]));
  autoTable(doc, { ...TABLE, startY: 140 + meta.length * 16 + 20, head: [head], body, foot: foot ? [foot] : undefined, styles: { ...TABLE.styles, fontSize: 8 }, columnStyles });
  return doc;
}

export const generateInvoicePDF = async (company, invoice, customer) => (await buildInvoicePDF(company, invoice, customer)).save(`${invoice.invoice_number || "invoice"}.pdf`);
export const generateStatementPDF = async (company, stmt) => (await buildStatementPDF(company, stmt)).save(`Statement-${(stmt.customer?.name||"customer").replace(/\s+/g,"_")}.pdf`);

export function waLink(phone, text) {
  const clean = String(phone||"").replace(/[^0-9]/g,"");
  const p = clean.length === 10 ? `91${clean}` : clean;
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
}

export async function shareViaWhatsApp({ doc, filename, phone, text }) {
  if (doc && typeof navigator !== "undefined" && navigator.canShare) {
    try {
      const file = new File([doc.output("blob")], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text, title: filename }); return "native"; }
    } catch (e) { if (e?.name === "AbortError") return "cancelled"; }
  }
  if (doc) doc.save(filename);
  window.open(waLink(phone, text), "_blank");
  return "link";
}

export const shareToWhatsApp = (phone, text) => window.open(waLink(phone, text), "_blank");
